import { NPCDef } from '../types';
import type { AvatarConfig } from '../../avatar/config';

/**
 * 许林辉捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对：
 * 写法统一成「库里 id ＋ 一眼读得出的描述」，不再混用「第几号」与「下标」两种记法。
 */
const XULINHUI_AVATAR: AvatarConfig = {
    gender: 'm',
    face: 5,      // mNormal 普通脸
    skin: 2,      // 肤色第 3 档 #efc49c
    hairBack: 0,  // 男款不画后发（renderer 的 isMale 分支），留 0 即可
    hairSides: 0, // 男款不画侧发
    hairFront: 0, // 男款不画刘海
    hair: 0,      // mHairNormal 普通头（男款的发型只看这一项）
    hairColor: 2, // 棕发 #8a5a3b
    brow: 1,      // sGrievous 委屈眉
    eye: 6,       // mEye3 男款眼睛 3
    eyeColor: 0,  // 瞳色第 1 档 #6b5d4f 深棕
    nose: 2,      // mNose1 男款鼻子 1
    noseDy: 1,    // 鼻子下移 1 格
    mouth: 4,     // mMouth1 嘴微张
    mouthDy: -1,  // 嘴上移 1 格
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 8,     // mSweater 男款毛衣（12 号 mSportswear 才是运动服）
    glasses: 2,   // mGlassesSquare 男款方框眼镜
    acc: 0,       // 无配饰
};

/**
 * 庄文茵捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对。
 */
const ZHUANGWENYIN_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 0,      // oval 鹅蛋脸
    skin: 1,      // 肤色第 2 档 #fdd3b4
    hairBack: 1,  // sBun 盘头
    hairSides: 0, // sSideNone 无侧发
    hairFront: 0, // none 无刘海
    hair: 0,      // 女款不用男款整头（renderer 走 isFemale 分支），填 0 即可
    hairColor: 1, // 发色第 2 档 #5d4333 深棕
    brow: 0,      // sHeroic 英气眉
    eye: 2,       // sGentle 温柔眼
    eyeColor: 0,  // 瞳色第 1 档 #6b5d4f 深棕
    nose: 1,      // sDefined 立体鼻
    noseDy: 0,    // 鼻子不下移
    mouth: 2,     // sPressed 抿嘴
    mouthDy: 0,   // 嘴不下移
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 3,     // sJacket 驼色夹克
    glasses: 0,   // 不戴眼镜
    acc: 0,       // 无配饰
};

/**
 * 罗一鸣捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对。
 */
const LUOYIMING_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 4,      // long 长脸
    skin: 1,      // 肤色第 2 档
    hairBack: 5,  // sLongWavy 长波浪
    hairSides: 2, // sSideLoose 披发
    hairFront: 3, // sMiddlePart 中分
    hair: 0,      // 女款不用男款整头（renderer 的 isFemale 分支），留 0 即可
    hairColor: 4, // 发色第 5 档
    brow: 0,      // sHeroic 英气眉
    eye: 0,       // sUpturned 上挑眼
    eyeColor: 7,  // 瞳色第 8 档
    nose: 1,      // sDefined 立体鼻
    noseDy: 1,    // 鼻子下移 1 格
    mouth: 0,     // sSmirk 嘴角微翘
    mouthDy: 1,   // 嘴下移 1 格
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 3,     // sJacket 驼色夹克
    glasses: 0,   // 不戴眼镜
    acc: 1,       // sHoopEarring 耳圈
};

/**
 * 乔子珊捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来，2026-10-20）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对。
 */
const QIAOZISHAN_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 3,      // pointed 瓜子脸
    skin: 0,      // 肤色第 1 档 #ffe3d0
    hairBack: 5,  // sLongWavy 长波浪
    hairSides: 1, // sSideBraid 耳后编发
    hairFront: 2, // sBangsWispy 齐刘海碎须
    hair: 0,      // 女款不用男款整头（renderer 的 isFemale 分支），留 0 即可
    hairColor: 2, // 发色第 3 档 #8a5a3b 棕发
    brow: 1,      // sGrievous 委屈眉
    eye: 1,       // sRound 圆眼
    eyeColor: 0,  // 瞳色第 1 档 #6b5d4f 深棕
    nose: 0,      // sSmall 小鼻
    noseDy: 0,    // 鼻子不下移
    mouth: 3,     // sSmile 笑
    mouthDy: 0,   // 嘴不下移
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 6,     // sBowShirt 蝴蝶结衬衫
    glasses: 0,   // 不戴眼镜
    acc: 0,       // 无配饰
};

/**
 * 黎青捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来，2026-11-03）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对。
 */
const LIQING_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 0,      // oval 鹅蛋脸
    skin: 0,      // 肤色第 1 档 #ffe3d0
    hairBack: 0,  // sBob 波波头
    hairSides: 0, // sSideNone 无侧发
    hairFront: 2, // sBangsWispy 齐刘海碎须
    hair: 0,      // 女款不用男款整头（renderer 的 isFemale 分支），留 0 即可
    hairColor: 2, // 发色第 3 档 #8a5a3b 棕发
    brow: 0,      // sHeroic 英气眉
    eye: 0,       // sUpturned 上挑眼
    eyeColor: 5,  // 瞳色第 6 档 #c4756b 红棕
    nose: 1,      // sDefined 立体鼻
    noseDy: 0,    // 鼻子不下移
    mouth: 1,     // sSlightlyOpen 嘴微张
    mouthDy: 0,   // 嘴不下移
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 5,     // sJacketSweater 蓝黄夹克
    glasses: 0,   // 不戴眼镜
    acc: 0,       // 无配饰
};

/**
 * 林静捏脸定稿（茄子在捏脸工坊拼好、走「导出配置」贴回来，2026-10-28）。
 * 工坊只吐 JSON 不带注释，下面的标注一律按 `avatar/parts.ts` 库里的 id 逐行核对。
 */
const MANAGER_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 4,      // long 长脸
    skin: 0,      // 肤色第 1 档 #ffe3d0
    hairBack: 3,  // sPonytail 马尾
    hairSides: 0, // sSideNone 无侧发
    hairFront: 0, // none 无刘海
    hair: 0,      // 女款不用男款整头（renderer 的 isFemale 分支），留 0 即可
    hairColor: 2, // 发色第 3 档 #8a5a3b 棕发
    brow: 2,      // sRaised 挑眉
    eye: 2,       // sGentle 温柔眼
    eyeColor: 1,  // 瞳色第 2 档 #3a3630 黑
    nose: 1,      // sDefined 立体鼻
    noseDy: 2,    // 鼻子下移 2 格
    mouth: 2,     // sPressed 抿嘴
    mouthDy: 3,   // 嘴下移 3 格
    beard: 0,     // 无胡须
    ageLines: 0,  // 无岁月痕迹
    cloth: 2,     // sSuit 西装
    glasses: 0,   // 不戴眼镜
    acc: 0,       // 无配饰
};

/** 经纪人（特殊 NPC）：置顶于普通 NPC 之外、无好感度、无 chat/invite/gift，只收工作通知与解约 */
export const MANAGER = {
    id: 'NPC_MANAGER',
    name: '林静',
    role: '经纪人',
    icon: '⭐',
    avatar: MANAGER_AVATAR,
} as const;

/**
 * NPC 池
 * 每个 NPC 有唯一 ID。
 *
 * 结识规则（V1，方案见 docs/玩法设计.md §八 + 实现说明迁存章 A3）：
 *  - initialFavor 统一 -1 = 未结识，不显示于人脉页；
 *  - 通告拍摄周按咖位概率偶遇（同剧组 + 剧本关联 NPC 进偶遇池）：初遇 VN 选「友好」才置 0 入页，
 *    选「冷漠」保持 -1，日后可再偶遇；已结识者熟遇 VN 带选项 +3~5（同 NPC 每月限 1 次）；
 *  - 结识后好感从 0 正常积累，五档：0 初识 / 20 认识 / 40 熟悉 / 60 好友 / 80 挚友（与 SocialSystem.getFavorLevel 同源）。
 *
 * 文案规则：
 *  - chatTexts / giftTexts 为文案池（string[]），每次操作随机取一条；先放一条，后续扩池不改结构；
 *  - chatTexts 为两段式对话「主角开场|NPC回复」：聊天由主角发起，先主角气泡再 NPC 气泡；
 *  - inviteOkText / inviteFailText 为单独字段（邀约成败语义不同，分开写）。
 */
export const NPCS: NPCDef[] = [
    // ⛔ 2026-10-06 茄子令：原 NPC_001~009 测试数据全删。特殊人脉六人（庄文茵/许林辉/穆遥/
    // 乔子珊/许澄/罗一鸣）以 NPCDef 形态进本表（社交全套：chatTexts/invite/gift 白嫖现有机制），
    // 特殊专用字段（结识多渠道/好感剧情钩子/竞争曲线）在附表 SpecialNpcs.ts——
    // 主表+附表模式与特殊剧本（SCRIPTS+SpecialScripts）对称，见 docs/特殊剧本实装设计.md §2.4。
    {
        // 特殊人脉：庄文茵（电影圈大满贯影后，绑定《承天》SCRIPT_902）。文案为茄子 2026-10-19 手写版；
        // 人设与取材见 docs/特殊人脉/庄文茵.md。特殊专用字段见 SpecialNpcs.ts。
        id: 'npc_zhuangwenyin',
        name: '庄文茵',
        role: '影后',
        gender: 'female',
        avatar: ZHUANGWENYIN_AVATAR,
        initialFavor: -1,
        relatedScripts: ['SCRIPT_902'],
        // 获奖感言（2026-10-21）：承天那场群嘲里的手艺派影后，不谈舆论、只谈戏；
        // 固定阵容参评后她也会是本池得主（见 SpecialNpcs.rank 与 ScriptDef.cast）。
        awardSpeechText: '演%角色名%那几个月，我每天都在跟自己较劲。谢谢《%作品名%》剧组，陪我较这个劲。这个奖，我收下了。',
        chatTexts: [
            '姐，您拍戏前会做很多功课吧？|当然//功课做在开机前，片场才不用慌',
            '听说您带新人特别挑？|主要看他较不较真//天赋我教不了，态度一眼看得出',
            '您也别太拼了，要注意身体呀|放心，我心里有数//真到拼不动那天，我自己会停',
            '我最近总觉得自己演得不对|不对就对了，觉得对的时候，往往最危险',
        ],
        greetingTexts: [
            'NPC|最近怎么样，有合适的戏吗？|在看呢姐，本子不少，能接的不多|挑就对了//接错一部，比没戏拍还伤人',
            'NPC|你说，演员是不是也该服老？|姐，您别这么想|不是我想服老，是我怕有一天，连不服的力气都没了',
            'NPC|最近一直在挑新人。|有合适的吗？|还没挑到//宁缺毋滥，挑错了毁一部戏',
        ],
        inviteOkText: '行，你挑地方，别太吵。',
        inviteFailText: '这两天不行，改天吧。',
        inviteMeetText: '坐。不知道你喝什么，给你点了杯美式。',
        giftTexts: [
            '东西我收了。你把戏演好，我就高兴，别破费这些。',
            '%s？你倒是有心，我留着了。',
        ],
        visitMeet: [
            '今天这条算是过了，难得。',
            '你来了？坐会儿，我歇口气。',
        ],
        firstMeet: [
            '我看过你的戏，还嫩，但认真。',
        ],
        firstWarm: [
            '嗯，难得，我还愿意认识个人。',
        ],
        encMetMeet: [
            '最近怎么样？',
        ],
        encMetChat: [
            '最近在看新人，看了一圈，认真的不多，有天赋的不缺，缺的是肯下笨功夫的。',
            '今天重看了一遍自己的旧戏，越看越不满意，以前觉得对的地方，现在全是问题。',
        ],
    },
    {
        // 特殊人脉：许林辉（导演，绑定《颠倒姻缘》SCRIPT_901）。文案为茄子 2026-10-10 手写版；
        // chatTexts 每条一个来回（`|` 左玩家右 NPC、`//` NPC 同方连发），格式见 docs/特殊人脉/模板文案格式.md §二.1。
        // encMetMeet 仅 1 条（格式最低 2 条，缺口待茄子补）。
        id: 'npc_xulinhui',
        name: '许林辉',
        role: '导演',
        gender: 'male',
        avatar: XULINHUI_AVATAR,
        initialFavor: -1,
        relatedScripts: ['SCRIPT_901'],
        chatTexts: [
            '最近在忙什么？|写了个新本子。',
            '你平时不拍戏的时候都做什么？|看片子，写东西，发呆。',
            '最近睡得好吗？|不太好。//闭上眼全是分镜，根本停不下来。',
            '你有没有想过转行？|想过很多次，但除了这个也不会干别的。',
        ],
        // NPC 主动打招呼文案池（2026-10-14 用户令：NPC| 前缀条目从 chatTexts 迁来，NPC 先开口的对话归招呼系统）
        greetingTexts: [
            'NPC|最近看了几部片子。|怎么样？|都不太好。//也可能是我眼光有问题。',
            'NPC|你有没有觉得我不适合干这行？|为什么这么问？|没什么。//就是随便问问。',
        ],
        // inviteFailText 单条字段：原稿两段以 // 分隔，已合并为一句（连发语法只适用于聊天池/熟遇池）
        inviteOkText: '行，你定地方吧。',
        inviteFailText: '改天吧，在忙。不过忙完也不一定有空。',
        inviteMeetText: '来了？坐吧。',
        giftTexts: [
            '谢了。',
            '我收下了，有心了。',
        ],
        // 探班开口句（影视城探班）：visitMeet=开口句；闲聊 reply 走 encMetChat（⛔ 独立 visitChat——已删）
        visitMeet: [
            '是你啊，好久不见了。',
            '最近怎么样？来这边坐。',
        ],
        // 初遇开口+反应（2026-10-15 新增：有 meet.events 时优先走剧情事件，时限过后走此字段兜底）
        firstMeet: [
            '初次见面，我是这部戏的导演许林辉。',
        ],
        firstWarm: [
            '请多指教。',
        ],
        encMetMeet: [
            '嗯，是你。',
        ],
        encMetChat: [
            '最近在写个新本子，写了一半又推翻了，习惯了。',
            '前两天看了部老片子，挺好，看完觉得自己拍的都不行。',
            '投资的事，也不知道能不能解决。',
            '最近没什么事，看片，写东西，发呆，一天就过去了。',
            '有个想法，想了好几年了，一直没动笔。',
        ],
    },
    {
        // 特殊人脉：罗一鸣（短剧转长剧，竞争型）。咖位为浮动值：登场 Lv.5 只是起点，此后随曲线涨
        // （锚点表见 docs/特殊人脉/README.md），台词里凡带「等消息/跑组」类新人处境的句子都受此约束，改文案先核对她当前咖位档。
        // 文案为茄子 2026-10-15 手写版；
        // 格式见 docs/特殊人脉/模板文案格式.md。特殊专用字段见 SpecialNpcs.ts。
        id: 'npc_luoyiming',
        name: '罗一鸣',
        role: '演员',
        gender: 'female',
        avatar: LUOYIMING_AVATAR,
        initialFavor: -1,
        relatedScripts: [],
        // 获奖感言（2026-10-20 茄子定稿）：颁奖夜得主感言优先念它；
        // 占位符取不到值或日后清空则回落四性格通用池，不会卡场。
        awardSpeechText: '谢谢评审，也谢谢《%作品名%》的导演和摄影组。我没别的本事，就是一部一部拍。这部戏，对得起进组那些天。',
        chatTexts: [
            '最近有戏吗？|没有，在等消息呢',
            '上次说的那个角色你去试了吗？|试了//估计悬，竞争的人太多了',
            '最近忙什么呢？|跑组//一天三个地儿，等通知',
            '周末打算干嘛？|应该是补觉吧//连轴转了一周，根本懒得动',
        ],
        greetingTexts: [
            'NPC|刚试完一个角色|怎么样，有把握吗？|不知道，导演全程没抬头',
            'NPC|这两天嗓子好干，难受|摸摸，还好吗？|秋天就犯毛病，年年这样',
        ],
        inviteOkText: '行。你定时间地点，我准时到。',
        inviteFailText: '这周不行，我赶戏呢。改天吧。',
        inviteMeetText: '我刚点完，你看看还要什么。',
        giftTexts: [
            '谢了。',
            '这个实用，我正缺。',
        ],
        visitMeet: [
            '来了？我这边刚收工，坐吧。',
            '正好，我拍完了，咱们找个地方说话。',
        ],
        firstMeet: [
            '%玩家名%对吧？你好，我是罗一鸣。',
        ],
        firstWarm: [
            '行，加个联系方式，对戏叫我啊。',
        ],
        encMetMeet: [
            '好久没见了。最近在哪儿发财呢？',
        ],
        encMetChat: [
            '最近跑了好几个组，都让等消息，害，习惯了。',
            '昨天试了个角色，跟导演聊了半天，成不成不知道，至少聊得舒服。',
            '这行就是，有的人不用试镜就有戏拍……算了，不管别人，我自己演好自己戏份就行。',
        ],
    },
    {
        // 特殊人脉：乔子珊（选秀出身，竞争型，绑定《偏航降落》SCRIPT_026 女主·夏初）。
        // 「二线小花」是登场档位而非终身档位：咖位随曲线浮动、封顶在一线到顶流之间（见 docs/特殊人脉/README.md 锚点表）。
        // 台词包 docs/特殊人脉/乔子珊.md 第二节：2026-10-20 AI 起草、茄子文档内逐条改过（多轮删短），
        // 导码按当日文档实物导入——池子还会随他改迭代，改文案先改文档再同步这里。
        // 特殊专用字段（rival/roleAnchors）见 SpecialNpcs.ts。
        id: 'npc_qiaozishan',
        name: '乔子珊',
        role: '演员',
        gender: 'female',
        avatar: QIAOZISHAN_AVATAR,
        initialFavor: -1,
        relatedScripts: [],
        awardSpeechText: '谢谢%奖项名%的各位评审，谢谢剧组每一个人，也谢谢一直支持我的粉丝们。演%角色名%的时候，我第一次觉得"做乔子珊"也可以不用那么完美。这份认可，我会好好收着。',
        chatTexts: [
            '感觉你最近好忙啊，是不是没怎么休息？|是啊//前两天都凌晨两点收工，六点又爬起来了，哭唧唧',
            '你那些采访，答得真漂亮|嘿嘿，我背了好久答案呢',
            '今天路过看到你粉丝给你的应援了，阵仗真大|我也看到啦，开心^^',
            '有点好奇，你们一起选秀那批人现在还有联系吗？|群里偶尔会冒个泡//大家都挺忙的，我一般就看看，发个表情',
        ],
        greetingTexts: [
            'NPC|你演戏的时候，跟平时不太一样|哪里不一样？|说不上来//就是比平时松弛，还挺好看的',
            'NPC|今天刷到了一个说我"营业模板化"的帖子|如何评价|写得很对啊//我给他点了个赞，我们运营气得跳脚，哈哈',
            'NPC|今天素颜出门了|被人认出来了吗？|没有，松了口气//……又有点失落，这是什么奇怪的毛病呀',
        ],
        inviteOkText: '好呀，你定地方吧？我没什么忌口，人少一点就好。',
        inviteFailText: '这段时间实在排不开，改天吧。等空下来，我请你呀。',
        inviteMeetText: '你来啦。我提前到了会儿，菜单都看过了，有几样还不错。',
        giftTexts: [
            '谢谢，我收下啦。',
            '%s？我找了好久呢，谢谢你，真的。',
        ],
        visitMeet: [
            '刚收工，正好能跟你说会儿话。',
            '你来得正好呀，我这场刚过。',
            '坐吧，我这边没事啦。',
        ],
        firstMeet: [
            '你好呀，我是乔子珊。我们是不是还没正式认识过呀？',
        ],
        firstWarm: [
            '可以呀，多个朋友总不是坏事。',
            '好呀，我记性可好了，你的名字我记住啦。',
        ],
        encMetMeet: [
            '正好，陪我说会儿话嘛。',
            '我今天脑子有点木，转不动啦。',
        ],
        encMetChat: [
            '最近在挑本子呀。看得上的够不着，够得着的看不上。挑来挑去，一个都没挑着，头都大了。',
            '前阵子看到个心动的本子，角色特别不讨喜。我纠结了好久，最后还是推了，现在又后悔了。',
        ],
    },
    {
        // 特殊人脉：黎青（竞争型 · 大器晚成演技派，2026-11-03 导入）。人设见 docs/特殊人脉/黎青.md。
        // 文案为茄子 2026-11-03 手写版；导码处理：
        // ①greetingTexts 补 NPC| 前缀，玩家回复段原稿 // 连发合并为一句（单段不拆气泡）；
        // ②inviteFailText 原稿 // 连发合并为一句。
        // 竞争型 rival/阶段门槛见 SpecialNpcs.ts（2026-11-03 配：开局 Lv.1 / Y1-7 上限 Lv.3 / Y8 起 Lv.7 且不接短剧、偶像/仙侠/恐怖）；初遇锚点剧情待茄子发。
        id: 'npc_liqing',
        name: '黎青',
        role: '演员',
        gender: 'female',
        avatar: LIQING_AVATAR,
        initialFavor: -1,
        relatedScripts: [],
        awardSpeechText: '感谢《%作品名%》所有工作人员，没有你们就没有这个角色。我跑了七年龙套，现在终于站到镜头前来了。',
        chatTexts: [
            '最近怎么样？|不好意思才看到//兼职太忙了，都没空去试镜了',
        ],
        greetingTexts: [
            'NPC|看到你的戏了，演得真好|谢谢~//也希望早日接到理想的角色|但愿如此',
        ],
        inviteOkText: '好啊，我准时到',
        inviteFailText: '最近太忙了，下次吧，不好意思。',
        inviteMeetText: '来了？正好来帮我看看这个剧本。',
        giftTexts: [
            '让你破费了。',
            '谢谢……不知道该回送给你什么才好……',
        ],
        visitMeet: [
            '我在等戏呢，一起坐吧。',
        ],
        firstMeet: [
            '你好，我叫黎青。你演什么角色？',
        ],
        firstWarm: [
            '好啊，以后常联系。',
        ],
        encMetMeet: [
            '%玩家名%？',
        ],
        encMetChat: [
            '什么时候才能演一个戏份重点的角色呢。',
        ],
    },
];

/** 按 ID 索引 */
export const NPC_MAP: Record<string, NPCDef> = {};
NPCS.forEach(n => { NPC_MAP[n.id] = n; });